import { Link } from 'react-router-dom';
import { useSession } from '../auth/SessionProvider.js';
import { Button, Card, PageHeader } from '../components/ui.js';
import { USPS_GUIDE } from '../help/uspsGuide.js';
import { VGS_GUIDE } from '../help/vgsGuide.js';
import { USPS_STEP_GUIDE } from '../help/uspsStepGuide.js';
import { VGS_STEP_GUIDE } from '../help/vgsStepGuide.js';
import { WORKFLOW_DOC } from '../help/workflowDoc.js';

/**
 * The three documents, chosen by what the reader is trying to do: follow a procedure,
 * look something up, or hand the workflow to USPS. Which step-by-step and which
 * reference guide is offered follows the organization type, so the VGS documents are
 * not a link a customer account could open.
 */
export function HelpPage() {
  const { isContractor } = useSession();
  const stepGuide = isContractor ? VGS_STEP_GUIDE : USPS_STEP_GUIDE;
  const referenceGuide = isContractor ? VGS_GUIDE : USPS_GUIDE;

  const documents = [
    {
      to: '/help/step-by-step',
      guide: stepGuide,
      heading: 'Start here',
      description:
        'Follow it in order. Every step says what to do, exactly where to do it, and what the system does in response.',
    },
    {
      to: '/help/reference',
      guide: referenceGuide,
      heading: 'Look something up',
      description:
        'The full reference: roles, statuses, thresholds, documents, reporting, and the rules behind each of them.',
    },
    {
      to: '/help/workflow',
      guide: WORKFLOW_DOC,
      heading: 'Share it',
      description:
        'The end-to-end workflow written to be handed to the Postal Service, as a Word document or printed to PDF.',
    },
  ];

  return (
    <div>
      <PageHeader
        title="Guides"
        subtitle="How to use VGS Work Order Management. Each document is also downloadable as a Word file."
      />

      {isContractor && VGS_GUIDE.video && (
        <div className="mb-4">
          <Card title={VGS_GUIDE.video.title}>
            <p className="mb-3 text-sm text-slate-700">{VGS_GUIDE.video.description}</p>
            <video
              controls
              preload="metadata"
              className="w-full max-w-4xl rounded-md border border-slate-200 bg-black"
              src={VGS_GUIDE.video.src}
            >
              <track kind="captions" />
            </video>
            <a
              href={VGS_GUIDE.video.src}
              download
              className="mt-2 inline-block text-sm font-medium text-brand-700 underline"
            >
              Download the video (MP4)
            </a>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {documents.map((document) => (
          <Card key={document.to}>
            <div className="flex h-full flex-col gap-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-brand-800">
                {document.heading}
              </p>
              <h2 className="text-base font-semibold text-slate-900">{document.guide.title}</h2>
              <p className="text-sm leading-6 text-slate-600">{document.description}</p>
              <p className="text-xs text-slate-500">
                {document.guide.sections.length} sections · {document.guide.audience}
              </p>
              <div className="mt-auto pt-2">
                <Link to={document.to}>
                  <Button variant="secondary">Open</Button>
                </Link>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
